import { useState } from 'react';
import { ChevronDown, ChevronUp, Info, Pencil } from 'lucide-react';
import { DEFAULTS, loadAssumptions, saveAssumptions } from '@/lib/savings';

export function AssumptionsFooter({ className = '' }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState(loadAssumptions);

  const handleSave = () => {
    saveAssumptions(values);
    setEditing(false);
  };

  const handleReset = () => {
    setValues({ ...DEFAULTS });
    saveAssumptions(DEFAULTS);
    setEditing(false);
  };

  return (
    <div className={`text-xs ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 text-[#A8A29E] hover:text-[#57534E] transition-colors"
        data-testid="assumptions-toggle"
      >
        <Info className="w-3 h-3" />
        <span>Assumptions (India avg): Diesel ₹{values.diesel_price_inr_per_litre}/L · DG {values.dg_kwh_per_litre} kWh/L · Grid ₹{values.grid_tariff_inr_per_kwh}/kWh</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div className="mt-3 p-4 rounded-xl bg-stone-50 border border-stone-200 space-y-3" data-testid="assumptions-drawer">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-[#A8A29E] mb-1">Diesel price</p>
              {editing ? (
                <input type="number" value={values.diesel_price_inr_per_litre} onChange={e => setValues(v => ({ ...v, diesel_price_inr_per_litre: +e.target.value }))}
                  className="w-full px-2 py-1 rounded border border-stone-300 text-sm mono" />
              ) : (
                <p className="font-medium text-[#1A1A1A] mono">₹{values.diesel_price_inr_per_litre}/L</p>
              )}
            </div>
            <div>
              <p className="text-[#A8A29E] mb-1">DG efficiency</p>
              {editing ? (
                <input type="number" step="0.1" value={values.dg_kwh_per_litre} onChange={e => setValues(v => ({ ...v, dg_kwh_per_litre: +e.target.value }))}
                  className="w-full px-2 py-1 rounded border border-stone-300 text-sm mono" />
              ) : (
                <p className="font-medium text-[#1A1A1A] mono">{values.dg_kwh_per_litre} kWh/L</p>
              )}
            </div>
            <div>
              <p className="text-[#A8A29E] mb-1">Grid tariff</p>
              {editing ? (
                <input type="number" step="0.5" value={values.grid_tariff_inr_per_kwh} onChange={e => setValues(v => ({ ...v, grid_tariff_inr_per_kwh: +e.target.value }))}
                  className="w-full px-2 py-1 rounded border border-stone-300 text-sm mono" />
              ) : (
                <p className="font-medium text-[#1A1A1A] mono">₹{values.grid_tariff_inr_per_kwh}/kWh</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <button onClick={handleSave} className="text-[#0061DD] font-medium hover:underline" data-testid="assumptions-save">Save</button>
                <button onClick={handleReset} className="text-[#A8A29E] hover:underline">Reset to defaults</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-[#0061DD] hover:underline" data-testid="assumptions-edit">
                <Pencil className="w-3 h-3" /> Edit assumptions
              </button>
            )}
          </div>
          <p className="text-[#A8A29E] leading-relaxed">
            National average values for India. Actual savings depend on your specific diesel consumption, grid tariff, and usage patterns.
            Upload electricity bills and DG logs during the quote process for a precise estimate.
          </p>
        </div>
      )}
    </div>
  );
}
